import type { MCarouselControlsType } from '../../molecules/MCarouselControls/MCarouselControls.types';

export enum OCardCarouselDesktopCards {
  One = '1',
  Two = '2',
  Three = '3',
  Four = '4',
  Six = '6',
}

export enum OCardCarouselTotalCards {
  One = '1',
  Two = '2',
  TwoPlus = '2+',
  Three = '3',
  ThreePlus = '3+',
  Four = '4',
  FourPlus = '4+',
  Six = '6',
  SixPlus = '6+',
}

/**
 * Raw AEM `mContentSwap` sub-model. The organism reads `mButton`/`mButton2`
 * from here when the flat top-level button fields are absent, so both
 * authoring patterns produce working CTAs.
 */
export interface OCardCarouselMContentSwap {
  type?: string;
  button2?: 'true' | 'false';
  listItem2?: 'true' | 'false';
  mButton?: {
    buttonText?: string;
    type?: string;
    ctaLink?: string;
    openInANewTab?: string;
    size?: string;
    icon?: string;
  };
  mButton2?: {
    buttonText?: string;
    type?: string;
    ctaLink?: string;
    openInANewTab?: string;
    size?: string;
    icon?: string;
  };
}

export interface OCardCarouselMContentHeader {
  alignment?: 'Left' | 'Centered';
  mTextBlockSimple?: {
    alignment?: 'Left' | 'Centered';
    aEyebrow?: {
      eyebrowText?: string;
    };
    aHeader?: {
      size?: string;
      text?: string;
      headerTag?: string;
    };
    aBody?: {
      bodyText?: string;
      size?: string;
      color?: string;
    };
  };
  mButton?: {
    buttonText?: string;
    type?: string;
    ctaLink?: string;
    openInANewTab?: string;
    size?: string;
    icon?: string;
  };
  mButton2?: {
    buttonText?: string;
    type?: string;
    ctaLink?: string;
    openInANewTab?: string;
    size?: string;
    icon?: string;
  };
  /**
   * AEM `mContentSwap` sub-model. When present, `mButton`/`mButton2` are
   * read from here as a fallback when the flat top-level button fields are
   * absent. `button2` gates the secondary CTA the same way `secondaryCta` does.
   */
  mContentSwap?: OCardCarouselMContentSwap;
  /** Toggles the primary CTA. */
  cta?: 'true' | 'false';
  /** Toggles the secondary CTA. */
  secondaryCta?: 'true' | 'false';
}

export interface OCardCarouselMCarouselControls {
  type?: MCarouselControlsType;
  imageOverlay?: string | boolean;
  arrows?: string | boolean;
}

export interface OCardCarouselTrackingProperties {
  location?: string;
  trackingTag?: string;
  trackingContentPosition?: string;
  trackingDescription?: string;
  trackingOfferType?: string;
  merchandisingCategory?: string;
  atCCeVar48?: string;
  clickTrack?: boolean;
  impressionTrack?: boolean;
  impressionCount?: boolean;
  enableScrollingBehavior?: boolean;
  leftArrowDesc?: string;
  cardLocation?: string;
  rightArrowDesc?: string;
  description?: string;
  faicMCD?: string;
  additionalTrackingVariables?: string;
}

export interface OCardCarouselCqItem {
  ':type'?: string;
  ':items'?: Record<string, OCardCarouselCqItem>;
  ':itemsOrder'?: string[];
  cqType?: string;
  cqItems?: Record<string, OCardCarouselCqItem>;
  cqItemsOrder?: string[];
  [key: string]: unknown;
}

export interface OCardCarouselModel {
  ':itemsOrder'?: string[];
  ':items'?: Record<string, OCardCarouselCqItem>;
  ':type'?: string;
  cqItemsOrder?: string[];
  cqItems?: Record<string, OCardCarouselCqItem>;
  cqType?: string;
  [key: string]: unknown;
}

export interface OCardCarouselProps {
  /** Number of cards visible at desktop. One of '1' | '2' | '3' | '4' | '6'. Default '3'. */
  desktopCards?: OCardCarouselDesktopCards | string;
  /**
   * Desktop-only visual hint. When true, the carousel viewport extends past
   * the 12-column grid so part of the next card is visible outside the grid.
   */
  peeking?: boolean | string;
  /**
   * Fixed AEM slot count. When provided, renders item0 through item{N-1} slots;
   * authored items fill matching slots and the rest stay empty. `children`
   * still take precedence over this prop.
   */
  noOfTotalCards?: number | string;
  mContentHeader?: OCardCarouselMContentHeader;
  mCarouselControls?: OCardCarouselMCarouselControls;
  pagePath?: string;
  itemPath?: string;
  aemReactComponentMapping?: Record<string, React.FC<Record<string, unknown>>>;
  /**
   * Storybook / non-AEM escape hatch. When provided, each top-level child becomes a slide
   * and AEM item data / `noOfTotalCards` are ignored.
   */
  children?: React.ReactNode;
  className?: string;
  trackingProperties?: OCardCarouselTrackingProperties;
  isAuthorMode?: boolean;
  /** AEM component ID used for DOM targeting in author mode. */
  componentId?: string;
  ':items'?: Record<string, OCardCarouselCqItem>;
  ':itemsOrder'?: string[];
  cqItems?: Record<string, OCardCarouselCqItem>;
  cqItemsOrder?: string[];
  model?: OCardCarouselModel;
}
